import { useMutation } from "@apollo/client"
import moment from "moment"
import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { ASSIGN_TEMP_BILLING_ACTIVITY_REPORT, ITempBillingPayment } from "./gql"

// kan-101. The activity report tag is per PAYMENT, not per invoice, and is only
// assignable once every invoice on the payment is settled (kan-112: cleared,
// accepted or in suspense) with zero unresolved orphans.
// It is the LAST step of the feature, so it lives in step 3 of the detail page's
// step bar and is drawn with that bar's tokens.
//
// The control mirrors the Direct Hire one in InvoiceAttachmentModal exactly: an
// <input type="month">, stored as the 'YYYY-MM' string, displayed MMM-YY. The
// new Date(value).toISOString().substring(0, 7) round-trip is an identity on a
// 'YYYY-MM' value (it parses as UTC midnight on the 1st) — kept verbatim so the
// two controls can never drift.
//
// Removing the tag is ALWAYS allowed: it is the escape hatch that re-enables
// reopening invoices, which the server blocks while a tag is assigned.

interface IActivityReportInputProps {
  payment?: ITempBillingPayment | null;
  refetch: Function;
}

const ActivityReportInput: FC<IActivityReportInputProps> = ({ payment, refetch }) => {
  const [activityReport, setActivityReport] = useState<string>('')

  const [assignActivityReport, { loading }] = useMutation(ASSIGN_TEMP_BILLING_ACTIVITY_REPORT, {
    onCompleted: () => {
      toast.success("Activity report updated")
      refetch()
    },
    onError: (error) => toast.error(error?.message || "something went wrong"),
  })

  useEffect(() => {
    setActivityReport(payment?.activityReportMonth || '')
  }, [payment?.activityReportMonth])

  const save = async (value: string | null) => {
    await assignActivityReport({
      variables: {
        input: {
          paymentMetaId: payment?.id,
          activityReportMonth: value,
        }
      }
    })
  }

  const assigned = payment?.activityReportMonth || ''
  // Assignable covers the "fully reconciled, zero orphans, stamped" gate; a
  // payment that already carries a tag can always have it removed.
  const canEdit = !!payment?.activityReportAssignable

  if (!canEdit && !assigned) {
    return (
      <span className="tbr-step-sub is-locked">
        Locked until every invoice is settled
      </span>
    )
  }

  return (
    <>
      <span className="tbr-step-sub">
        {assigned
          ? `Reported as ${moment(assigned).format('MMM-YY')} — remove it before reopening invoices`
          : 'Tag the month this payment is reported in'}
      </span>

      <div className="tbr-activity">
        <input
          className="tbr-month-input"
          type="month"
          title="Activity report month"
          value={activityReport}
          disabled={!canEdit || loading}
          max={new Date().toISOString().substring(0, 7)}
          onChange={({ target: { value } }) => {
            if (value) setActivityReport(new Date(value).toISOString().substring(0, 7))
            else setActivityReport('')
          }}
        />
        <button
          type="button"
          className="tbr-btn is-sm is-action"
          disabled={loading || !canEdit || activityReport === assigned}
          onClick={() => save(activityReport || null)}
        >
          Save
        </button>
        {!!assigned && (
          <button
            type="button"
            className="tbr-btn is-sm is-outline-neutral"
            disabled={loading}
            onClick={() => save(null)}
          >
            Remove
          </button>
        )}
      </div>
    </>
  )
}

export default ActivityReportInput
